import { getAuthToken, getLastAuthError } from '../lib/authToken';

const API_BASE_URL = import.meta.env.VITE_API_URL?.replace(/\/$/, '');

export type AdminDlqItem = {
  id: string;
  key: string;
  quarantineKey?: string;
  lastError?: string;
  scanAttempts?: number;
  scanCompletedAt?: string;
  nextRetryAt?: string;
  retryInSeconds?: number;
  retryUrl?: string;
  forceRetryUrl?: string;
};

type DlqResponse = { items: AdminDlqItem[] };

const mockItems: AdminDlqItem[] = [
  {
    id: 'mock-failed-1',
    key: 'candidates/cv/user/mock-failed-1.pdf',
    quarantineKey: 'quarantine/errors/mock-failed-1',
    lastError: 'Timeout contacting scanner',
    scanAttempts: 5,
    retryInSeconds: 0,
  },
];

export async function fetchAdminDlqUploads() {
  if (!API_BASE_URL) {
    throw new Error('VITE_API_URL is required to load admin upload DLQ.');
  }
  const token = await getAuthToken();
  if (!token) {
    const reason = getLastAuthError() ?? 'no access token from Auth0';
    throw new Error(`DLQ fetch blocked: ${reason}`);
  }
  const response = await fetch(`${API_BASE_URL}/v1/admin/uploads/dlq`, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: 'include',
  });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`DLQ fetch failed (${response.status}): ${text}`);
  }
  const payload = (await response.json()) as DlqResponse;
  return payload.items ?? [];
}

export async function retryAdminUpload(uploadId: string, force?: boolean) {
  if (!API_BASE_URL) {
    throw new Error('VITE_API_URL is required to retry admin uploads.');
  }
  const token = await getAuthToken();
  if (!token) {
    const reason = getLastAuthError() ?? 'no access token from Auth0';
    throw new Error(`Retry blocked: ${reason}`);
  }
  const url = new URL(`${API_BASE_URL}/v1/admin/uploads/dlq/${uploadId}/retry`);
  if (force) url.searchParams.set('force', 'true');
  const response = await fetch(url.toString(), {
    method: 'POST',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: 'include',
  });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`Retry failed (${response.status}): ${text}`);
  }
  return true;
}

export async function deleteAdminUpload(uploadId: string) {
  if (!API_BASE_URL) {
    throw new Error('VITE_API_URL is required to delete admin uploads.');
  }
  const token = await getAuthToken();
  if (!token) {
    const reason = getLastAuthError() ?? 'no access token from Auth0';
    throw new Error(`Delete blocked: ${reason}`);
  }
  const response = await fetch(`${API_BASE_URL}/v1/admin/uploads/dlq/${uploadId}/delete`, {
    method: 'POST',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: 'include',
  });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`Delete failed (${response.status}): ${text}`);
  }
  return true;
}
